/**
 * InvitePlayerSearch — lobby widget for host-side player discovery.
 *
 * Renders a debounced search input, results list (with avatar + handle +
 * display name), and an Invite button per row. Tapping Invite hits the
 * `POST /sessions/:id/invite` endpoint which best-effort sends a push
 * notification to the target user containing the game's invite code.
 *
 * The component intentionally stops short of adding the invited user as a
 * SessionPlayer — invitations are pull-based, so the target still has to
 * accept by joining. Until they do, they don't appear in the player list.
 *
 * Render only in the LOBBY status. Outside lobby, the search box is dead
 * weight (the game is closed to new players).
 */

import React, { useEffect, useState } from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native'
import { useQueryClient } from '@tanstack/react-query'
import { Input } from '../ui/Input'
import type { UserSearchResult, UserSearchResponse } from '@warcarp/api-types'
import { PlayerVacationIndicator } from '../settings/PlayerVacationIndicator'
import { apiGet, apiPost } from '../../lib/api'

const DEBOUNCE_MS = 350
const MIN_QUERY_LEN = 2

interface InviteResponse {
  invited: { id: string; username: string; displayName: string }
  pushed: boolean
  emailed: boolean
}

interface Props {
  gameId: string
  /** Set of userIds already in the game — used to dim/hide them. */
  existingUserIds: Set<string>
  /**
   * Set of userIds who DECLINED an invite to this game. Declining is final:
   * the API 409s on re-invite, so their row renders a disabled "Declined"
   * button instead of an Invite button.
   */
  declinedUserIds?: Set<string>
  /**
   * Set of userIds with a PENDING invite to this game — their row renders a
   * disabled "Invited" button (same treatment as Declined) rather than the
   * misleading "// in lobby" copy that players get.
   */
  invitedUserIds?: Set<string>
  /**
   * When true, render without the surrounding `bg-paper` card / outer
   * margin — the host renders this inside its own consolidated panel
   * and supplies the chrome.
   */
  embedded?: boolean
  /** No chrome and no "Find Player" label: the parent card supplies both. */
  bare?: boolean
  placeholder?: string
  autoFocus?: boolean
}

export function InvitePlayerSearch({
  gameId,
  existingUserIds,
  declinedUserIds,
  invitedUserIds,
  embedded,
  bare,
  placeholder = 'Player Name',
  autoFocus,
}: Props) {
  const [query, setQuery] = useState('')
  const qc = useQueryClient()
  const [results, setResults] = useState<UserSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [invitingId, setInvitingId] = useState<string | null>(null)
  // Track which users this host has already invited THIS session — purely
  // a local UX hint ("Invited" instead of "Invite"). Doesn't survive a
  // page refresh, which is fine: backend dedup isn't free, and a second
  // invite is harmless (it's just another push).
  const [invitedIds, setInvitedIds] = useState<Set<string>>(() => new Set())
  // Inline error for search / invite — no toasts in this admin area. (The
  // "notifications off" hint now lives on the host's Pending invites row, keyed
  // off the invitation's `notifiable` flag.)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LEN) {
      setResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const res = await apiGet<UserSearchResponse>(
          `/api/v1/users/search?q=${encodeURIComponent(trimmed)}&limit=10`,
        )
        // Guard against stale responses overwriting newer ones.
        setQuery((curr) => {
          if (curr.trim() === trimmed) {
            setResults(res.results)
            setSearching(false)
          }
          return curr
        })
      } catch (err) {
        setSearching(false)
        setError(err instanceof Error ? err.message : 'Search failed')
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  async function handleInvite(user: UserSearchResult) {
    setInvitingId(user.id)
    setError(null)
    try {
      await apiPost<InviteResponse>(`/api/v1/games/${gameId}/invite`, {
        userId: user.id,
      })
      // The row flips to "✓ Invited" via invitedIds — that's confirmation
      // enough. The invitee now appears in the host's Pending invites list,
      // which carries the "notifications off — share the code" hint when needed.
      setInvitedIds((curr) => {
        const next = new Set(curr)
        next.add(user.id)
        return next
      })
      // Refresh the game detail so the new pending invite shows in the roster
      // immediately, instead of waiting for the fallback poll. The invite
      // endpoint returns only the invite record (not the full game), so we
      // invalidate rather than setQueryData.
      void qc.invalidateQueries({ queryKey: ['games', gameId] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed')
    } finally {
      setInvitingId(null)
    }
  }

  const containerClass = bare
    ? 'gap-2'
    : embedded
      ? 'bg-paper-up border border-olive rounded-md p-3 gap-2'
      : 'mx-4 mt-3 bg-paper rounded-xl p-3 gap-2'
  return (
    <View className={containerClass}>
      {!bare && (
        <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">
          Find Player
        </Text>
      )}
      <Input
        value={query}
        onChangeText={(v) => {
          setQuery(v)
          if (error) setError(null)
        }}
        placeholder={placeholder}
        accessibilityLabel={placeholder}
        autoFocus={autoFocus}
        autoCapitalize="none"
        autoComplete="off"
        autoCorrect={false}
      />
      {error && <Text className="font-typed text-xs text-blood">{error}</Text>}
      {query.trim().length >= MIN_QUERY_LEN && (
        <View className="gap-1">
          {searching && (
            <View className="flex-row items-center gap-2 py-2">
              <ActivityIndicator size="small" />
              <Text className="font-typed text-xs text-ink-soft italic">// searching…</Text>
            </View>
          )}
          {!searching && results.length === 0 && (
            <Text className="font-typed text-xs text-ink-soft italic py-1">// no matches</Text>
          )}
          {!searching &&
            results.map((u) => {
              const alreadyInSession = existingUserIds.has(u.id)
              // Pending invite from the server OR just invited this session
              // (the local set bridges the gap until the refetch lands).
              const alreadyInvited = invitedIds.has(u.id) || (invitedUserIds?.has(u.id) ?? false)
              const declined = declinedUserIds?.has(u.id) ?? false
              return (
                <View
                  key={u.id}
                  className="flex-row items-center justify-between bg-paper-up rounded-md px-3 py-2"
                >
                  <View className="flex-1 mr-3">
                    <View className="flex-row items-center gap-2 flex-wrap">
                      <Text className="font-stencil text-ink text-sm">{u.displayName}</Text>
                      <PlayerVacationIndicator name={u.displayName} vacation={u.vacation} showRemaining />
                    </View>
                    <Text className="font-typed text-ink-soft text-xs">@{u.username}</Text>
                  </View>
                  {declined ? (
                    // Final for this game — the API refuses re-invites, so
                    // the button is disabled rather than an error on tap.
                    <TouchableOpacity
                      disabled
                      accessibilityLabel={`${u.displayName} declined an invitation`}
                      accessibilityRole="button"
                      accessibilityState={{ disabled: true }}
                      className="bg-paper rounded-md px-3 py-1.5 border border-olive/40 opacity-60"
                    >
                      <Text className="font-stencil text-ink-soft text-xs uppercase">Declined</Text>
                    </TouchableOpacity>
                  ) : alreadyInvited ? (
                    <TouchableOpacity
                      disabled
                      accessibilityLabel={`${u.displayName} has a pending invitation`}
                      accessibilityRole="button"
                      accessibilityState={{ disabled: true }}
                      className="bg-paper rounded-md px-3 py-1.5 border border-olive/40 opacity-60"
                    >
                      <Text className="font-stencil text-ink-soft text-xs uppercase">Invited</Text>
                    </TouchableOpacity>
                  ) : alreadyInSession ? (
                    <Text className="font-typed text-ink-soft text-xs italic">// in lobby</Text>
                  ) : (
                    <TouchableOpacity
                      onPress={() => handleInvite(u)}
                      disabled={invitingId === u.id}
                      accessibilityLabel={`Invite ${u.displayName}`}
                      accessibilityRole="button"
                      className="bg-blood rounded-md px-3 py-1.5"
                    >
                      <Text className="font-stencil text-white text-xs uppercase">
                        {invitingId === u.id ? '…' : 'Invite'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              )
            })}
        </View>
      )}
    </View>
  )
}
